import { Siddi } from "@creately/siddi";
import {
  getGravityUserIdFromCookie,
  getGravityUserPlanFromCookie,
  getGravityUserStatusFromCookie,
} from "../../auth/utils/gravityTokenClaims";

type PostHogClient = {
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  __loaded?: boolean;
};

type BrowserWindowLike = {
  location: { pathname: string };
  __CREATELY_USER_PLAN__?: string;
  __CREATELY_USER_ID__?: string;
};

export class Tracker {
  protected static _siddi: any;
  protected static _globals: Record<string, unknown> = {};
  private static _posthog: PostHogClient | null = null;
  private static _posthogLoading: Promise<void> | null = null;

  public static get globals() {
    return this._globals;
  }

  static setGlobal(key: string, value: unknown): void {
    if (value === undefined || value === null) return;
    this._globals[key] = value;
  }

  static setGlobals(values: Record<string, unknown>): void {
    Object.entries(values).forEach(([key, value]) => this.setGlobal(key, value));
  }

  private static getBrowserWindow(): BrowserWindowLike | null {
    const candidate = (globalThis as { window?: unknown }).window;
    if (!candidate || typeof candidate !== "object") return null;
    const maybe = candidate as Partial<BrowserWindowLike>;
    if (!maybe.location || typeof maybe.location.pathname !== "string") return null;
    return maybe as BrowserWindowLike;
  }

  private static formatPlan(plan: string | null | undefined): string {
    if (typeof plan !== "string") return "UNKNOWN";
    const segment = plan.split("-")[0]?.trim();
    if (!segment) return "UNKNOWN";
    return segment.toUpperCase();
  }

  private static resolvePlan(): string {
    const browserWindow = this.getBrowserWindow();
    if (!browserWindow) {
      return typeof this._globals.plan === "string"
        ? (this._globals.plan as string)
        : "UNKNOWN";
    }

    if (typeof this._globals.plan === "string" && this._globals.plan.length > 0) {
      return this._globals.plan as string;
    }

    let plan: string | null = null;
    let status: string | null = null;

    try {
      plan = getGravityUserPlanFromCookie();
    } catch (error) {
      console.warn("Tracker: failed to read gravity user plan", error);
    }

    try {
      status = getGravityUserStatusFromCookie();
    } catch (error) {
      console.warn("Tracker: failed to read gravity user status", error);
    }

    if (!plan) {
      plan =
        browserWindow.__CREATELY_USER_PLAN__ ??
        (import.meta as { env?: { VITE_USER_PLAN?: string } }).env?.VITE_USER_PLAN ??
        null;
    }

    const normalizedStatus = typeof status === "string" ? status.trim().toUpperCase() : null;
    this._globals.plan = normalizedStatus === "DEMO" ? "DEMO" : this.formatPlan(plan);
    return this._globals.plan as string;
  }

  private static resolveUserId(): string {
    const browserWindow = this.getBrowserWindow();
    if (!browserWindow) {
      return typeof this._globals.userId === "string" ? (this._globals.userId as string) : "anonymous";
    }

    if (typeof this._globals.userId === "string" && this._globals.userId.length > 0) {
      return this._globals.userId as string;
    }

    let userId: string | null = null;

    try {
      userId = getGravityUserIdFromCookie();
    } catch (error) {
      console.warn("Tracker: failed to read gravity user id", error);
    }

    if (!userId) {
      userId =
        browserWindow.__CREATELY_USER_ID__ ??
        "anonymous";
    }

    this._globals.userId = userId ?? "anonymous";
    return this._globals.userId as string;
  }

  static track(eventName: string, eventProperties?: Record<string, unknown>): void {
    try {
      if (!this._siddi) {
        this._siddi = new Siddi([
          { name: "ga4" },
          { name: "sendinblue", deny: ["a.growthbook.experiment.viewed"] },
        ]);
      }

      const props = eventProperties ?? {};
      const appEventName = `a.${eventName}`;
      const plan =
        typeof this._globals.plan === "string" && this._globals.plan.length > 0
          ? (this._globals.plan as string)
          : this.resolvePlan();
      const userId =
        typeof this._globals.userId === "string" && this._globals.userId.length > 0
          ? (this._globals.userId as string)
          : this.resolveUserId();
      const browserWindow = this.getBrowserWindow();
      const mergedProperties = {
        url: browserWindow?.location.pathname,
        ...this._globals,
        ...props,
        plan,
        userId,
        user_id: userId,
        eventCategory: "AI App",
      };
      this._siddi.track(appEventName, mergedProperties);
      if (!browserWindow) return;
      const isDashboard = browserWindow.location.pathname.startsWith("/dashboard");
      if (isDashboard) {
        return;
      }
      this.captureToPostHog(appEventName, mergedProperties);
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  }

  private static captureToPostHog(eventName: string, properties: Record<string, unknown>): void {
    if (this._posthog) {
      if (this._posthog.capture && this._posthog.__loaded) {
        this._posthog.capture(eventName, properties);
      }
      return;
    }

    if (!this._posthogLoading) {
      this._posthogLoading = import("posthog-js").then((module) => {
        this._posthog = module.default as PostHogClient;
        if (this._posthog?.capture && this._posthog.__loaded) {
          this._posthog.capture(eventName, properties);
        }
      }).catch((err) => {
        console.warn("Failed to load PostHog:", err);
      });
    }
  }
}
